/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import { AnyAction } from "redux";
import { ofType, StateObservable } from "redux-observable";
import { of, Observable, catchError, from, repeat, defer } from "rxjs";
import {
  filter,
  last,
  map,
  mergeMap,
  switchMap,
  takeWhile,
} from "rxjs/operators";
import { IAppState } from "../types";
import { MAX_DEVICES_LIMIT } from "@nf/constants";
import {
  AuthorizationHelper,
  StorageHelper,
  userProfileHelper,
} from "../../helpers";
import { CookieHelper } from "../../helpers/CookieHelper";
import { IErrorModel, IUserConsentModel, toErrorModel } from "../../models";
import { DataProvider } from "../../providers";
import { offeringIdsService } from "../../services";
import { refreshUser, getSessionDetails, updateTokens } from "../auth/actions";
import {
  deleteUserDeviceFailure,
  deleteUserDeviceSuccess,
  getProfileFailure,
  getProfileSuccess,
  getUserFavoritesSuccess,
  getUserFavoritesFailure,
  getUserConsentsFailure,
  getUserConsentsSuccess,
  getUserDevicesFailure,
  getUserDevicesSuccess,
  updateProfileFailure,
  updateProfileSuccess,
  updateUserConsentsFailure,
  updateUserConsentsSuccess,
  getWatchHistoryFailure,
  getWatchHistorySuccess,
  deleteWatchHistorySuccess,
  deleteWatchHistoryFailure,
  getUserSubscriptionsSuccess,
  getUserSubscriptionsFailure,
  getUserAvailabilityKeysFailure,
  getUserAvailabilityKeysSuccess,
  createUserSubprofilesSuccess,
  createUserSubprofilesFailure,
  getUserSubprofilesSuccess,
  getUserSubprofilesFailure,
  deleteUserSubprofileSuccess,
  deleteUserSubprofileFailure,
  updateUserSubprofileSuccess,
  updateUserSubprofileFailure,
  selectUserSubprofileSuccess,
  selectUserSubprofileFailure,
  getUserFavorites,
  getUserParentalRatingsSuccess,
  getUserParentalRatingsFailure,
  setUserSubprofilePinCodeSuccess,
  setUserSubprofilePinCodeFailure,
  deleteUserSubprofilePinCodeSuccess,
  deleteUserSubprofilePinCodeFailure,
  getProductOfferingIdsSuccess,
  getProductOfferingIdsFailure,
  checkOrderAndFetchSubscriptionsFailure,
  checkOrderAndFetchSubscriptionsSuccess,
  getUserSubscriptions,
  getUserCountrySuccess,
  getUserCountryFailure,
} from "./actions";
import * as Consts from "./consts";
import {
  IGetProfileAction,
  IDeleteUserDeviceAction,
  IGetUserFavoritesAction,
  IGetUserConsentsAction,
  IGetUserDevicesAction,
  IUpdateProfileAction,
  IUpdateProfileSuccessAction,
  IUpdateUserConsentsAction,
  IDeleteUserDeviceSuccessAction,
  IGetWatchHistoryAction,
  IDeleteWatchHistoryAction,
  IGetUserSubscriptionsAction,
  IGetUserAvailabilityKeysAction,
  ICreateUserSubprofilesAction,
  IGetUserSubprofilesAction,
  IDeleteUserSubprofilesAction,
  IEditUserSubprofileAction,
  ISelectUserSubprofileAction,
  ISelectUserSubprofileSuccessAction,
  IGetUserParentalRatingsAction,
  ISetUserSubprofilePinCodeAction,
  IDeleteUserSubprofilePinCodeAction,
  IGetProfileSuccessAction,
  IGetUserSubscriptionsSuccessAction,
  IGetProductOfferingIdsAction,
  ICheckOrderAndFetchSubscriptionsAction,
  IGetUserCountryAction,
} from "./types";
import { deleteAllMediaAction } from "../media/actions";
import { updateApplicationMenu } from "../configuration/actions";
import * as Types from "../auth/types";
import { OrderStatus } from "../../providers/DataProvider/Skymill/models";
import { UserConsentsNames } from "../../enums";

const getProfileEpic = (action$: Observable<IGetProfileAction>) =>
  action$.pipe(
    ofType(Consts.GET_PROFILE),
    switchMap(async () => {
      try {
        const user = await DataProvider.getProfile();

        return getProfileSuccess(user);
      } catch (error) {
        return getProfileFailure(error as IErrorModel);
      }
    })
  );

const updateProfileEpic = (action$: Observable<IUpdateProfileAction>) =>
  action$.pipe(
    ofType(Consts.UPDATE_PROFILE),
    switchMap(async (action: IUpdateProfileAction) => {
      try {
        const user = await DataProvider.updateProfile(action.payload);

        return updateProfileSuccess(user);
      } catch (error) {
        return updateProfileFailure(error as IErrorModel);
      }
    })
  );

const getUserFavoritesEpic = (action$: Observable<IGetUserFavoritesAction>) =>
  action$.pipe(
    ofType(Consts.GET_USER_FAVORITES),
    switchMap(async () => {
      try {
        const userAssetsProperties = await DataProvider.getUserFavorites();
        return getUserFavoritesSuccess(userAssetsProperties);
      } catch (error) {
        return getUserFavoritesFailure(error as IErrorModel);
      }
    })
  );

const updateProfileSuccessEpic = (
  action$: Observable<IUpdateProfileSuccessAction>
) =>
  action$.pipe(
    ofType(Consts.UPDATE_PROFILE_SUCCESS),
    switchMap((action: IUpdateProfileSuccessAction) => {
      return of(refreshUser(action.payload));
    })
  );

const getUserDevicesEpic = (action$: Observable<IGetUserDevicesAction>) =>
  action$.pipe(
    ofType(Consts.GET_USER_DEVICES),
    switchMap(async () => {
      try {
        const [devices, playDevices] = await Promise.all([
          DataProvider.getUserDevices(),
          DataProvider.getUserPlayDevices(),
        ]);

        const playDeviceIds = new Set(playDevices.map((p) => p.Id));
        const allDevices = devices.map((device) => ({
          ...device,
          PlayDevice: playDeviceIds.has(device.Id),
        }));

        // Add missing (orphaned) playDevices as Unknown
        for (const playDevice of playDevices) {
          if (!devices.some((device) => device.Id === playDevice.Id)) {
            allDevices.push({
              Id: playDevice.Id,
              Name: "Unknown",
              CurrentDevice: false,
              PlayDevice: true,
            });
          }
        }

        return getUserDevicesSuccess(allDevices);
      } catch (error) {
        return getUserDevicesFailure(error as IErrorModel);
      }
    })
  );

const deleteUserDeviceEpic = (action$: Observable<IDeleteUserDeviceAction>) =>
  action$.pipe(
    ofType(Consts.DELETE_USER_DEVICE),
    switchMap(async (action: IDeleteUserDeviceAction) => {
      try {
        await Promise.all([
          DataProvider.deleteUserDevice(action.payload),
          DataProvider.deleteUserPlayDevice(action.payload),
        ]);

        return deleteUserDeviceSuccess(action.payload);
      } catch (error) {
        return deleteUserDeviceFailure(error as IErrorModel);
      }
    })
  );

const deleteUserDeviceSuccessEpic = (
  action$: Observable<IDeleteUserDeviceSuccessAction>,
  state$: StateObservable<IAppState>
) =>
  action$.pipe(
    ofType(Consts.DELETE_USER_DEVICE_SUCCESS),
    switchMap(async () =>
      state$.value.user.devices.Data?.length === MAX_DEVICES_LIMIT
        ? getSessionDetails()
        : { type: "NEVER" }
    )
  );

const getUserConsentsEpic = (
  action$: Observable<IGetUserConsentsAction | Types.ISyncWebUserSuccessAction>
) =>
  action$.pipe(
    ofType(Consts.GET_USER_CONSENTS),
    switchMap(async () => {
      try {
        const consents = await DataProvider.getUserConsents();
        return getUserConsentsSuccess(consents);
      } catch (error) {
        return getUserConsentsFailure(error as IErrorModel);
      }
    })
  );

const updateUserConsentsEpic = (
  action$: Observable<IUpdateUserConsentsAction>
) =>
  action$.pipe(
    ofType(Consts.UPDATE_USER_CONSENTS),
    switchMap(async ({ payload }) => {
      try {
        const data = await DataProvider.updateUserConsents(payload);

        const marketingConsent = data.find((consent) =>
          consent.ConsentName.includes(UserConsentsNames.Marketing)
        );

        if (marketingConsent) {
          CookieHelper.setMarketing(marketingConsent.Accepted);
        }

        return updateUserConsentsSuccess(data);
      } catch (error) {
        return updateUserConsentsFailure(toErrorModel(error));
      }
    })
  );

const getWatchHistoryEpic = (action$: Observable<IGetWatchHistoryAction>) =>
  action$.pipe(
    ofType(Consts.GET_WATCH_HISTORY),
    switchMap(async () => {
      try {
        const response = await DataProvider.getUserWatchHistory();

        return getWatchHistorySuccess(response);
      } catch (error) {
        return getWatchHistoryFailure(error as IErrorModel);
      }
    })
  );

const deleteWatchHistoryEpic = (
  action$: Observable<IDeleteWatchHistoryAction>
) =>
  action$.pipe(
    ofType(Consts.DELETE_WATCH_HISTORY),
    switchMap(async (action: IDeleteWatchHistoryAction) => {
      try {
        await DataProvider.deleteUserWatchHistory(action.payload);

        return deleteWatchHistorySuccess(action.payload);
      } catch (error) {
        return deleteWatchHistoryFailure(error as IErrorModel);
      }
    })
  );

const getUserSubscriptionsEpic = (
  action$: Observable<IGetUserSubscriptionsAction | IGetProfileSuccessAction>
) =>
  action$.pipe(
    ofType(Consts.GET_USER_SUBSCRIPTIONS, Consts.GET_PROFILE_SUCCESS),
    switchMap(
      async (): Promise<boolean> => await AuthorizationHelper.isAuthenticated()
    ),
    filter((authenticated) => authenticated),
    switchMap(async () => {
      try {
        const result = await DataProvider.getUserSubscriptions();
        return getUserSubscriptionsSuccess(result);
      } catch (error) {
        return getUserSubscriptionsFailure(error as IErrorModel);
      }
    })
  );

const getUserAvailabilityKeysEpic = (
  action$: Observable<
    IGetUserAvailabilityKeysAction | IGetUserSubscriptionsSuccessAction
  >
) =>
  action$.pipe(
    ofType(
      Consts.GET_USER_AVAILABILITY_KEYS,
      Consts.GET_USER_SUBSCRIPTIONS_SUCCESS
    ),
    switchMap(async () => {
      try {
        const availabilityKeys = await DataProvider.getUserAvailabilityKeys();
        return getUserAvailabilityKeysSuccess(availabilityKeys);
      } catch (error) {
        return getUserAvailabilityKeysFailure(error as IErrorModel);
      }
    })
  );

const getProductOfferingIdsEpic = (
  action$: Observable<IGetProductOfferingIdsAction>
) =>
  action$.pipe(
    ofType(Consts.GET_PRODUCT_OFFERING_IDS),
    switchMap(async () => {
      try {
        const payload = await DataProvider.getProductOfferingIds();
        offeringIdsService.setOfferingIds(payload);
        return getProductOfferingIdsSuccess(payload);
      } catch (error) {
        return getProductOfferingIdsFailure(error as IErrorModel);
      }
    })
  );

const getUserSubprofilesEpic = (
  action$: Observable<IGetUserSubprofilesAction>,
  state$: StateObservable<IAppState>
) =>
  action$.pipe(
    ofType(Consts.GET_USER_SUBPROFILES),
    switchMap(async () => await StorageHelper.getCurrentSubprofileId()),
    switchMap(async (currentSubProfileId) => {
      const currentSubProfileFromRedux =
        state$.value.user.subprofiles.Data?.currentSubprofile;

      return {
        currentSubProfileId,
        currentSubProfileFromRedux,
      };
    }),
    switchMap(({ currentSubProfileId, currentSubProfileFromRedux }) =>
      from(
        (async () => {
          try {
            const [pinCodeId, subProfiles] = await Promise.all([
              DataProvider.getUserSubprofilePinCode(),
              DataProvider.getUserSubprofiles(
                currentSubProfileId ?? currentSubProfileFromRedux?.UserId
              ),
            ]);

            let currentSubProfile =
              subProfiles.length === 1
                ? subProfiles[0]
                : currentSubProfileId && !currentSubProfileFromRedux
                ? subProfiles.find(
                    (subProfile) => subProfile.UserId === currentSubProfileId
                  )
                : currentSubProfileFromRedux;

            if (subProfiles.length > 1) {
              userProfileHelper.sortSubProfiles(subProfiles);
            }

            if (!currentSubProfile && subProfiles.length > 0) {
              currentSubProfile = subProfiles.filter(
                (subProfile) => subProfile.CurrentProfile === true
              )[0];
            }

            return getUserSubprofilesSuccess(
              subProfiles,
              currentSubProfile,
              pinCodeId
            );
          } catch (error) {
            return getUserSubprofilesFailure(error as IErrorModel);
          }
        })()
      )
    )
  );

const createUserSubprofileEpic = (
  action$: Observable<ICreateUserSubprofilesAction>,
  state$: StateObservable<IAppState>
) =>
  action$.pipe(
    ofType(Consts.CREATE_USER_SUBPROFILES),
    switchMap(async (action: ICreateUserSubprofilesAction) => {
      try {
        const currentSubProfile =
          state$.value.user.subprofiles.Data?.currentSubprofile;
        const currentSubprofileId = currentSubProfile?.UserId;

        const subprofiles = await DataProvider.createUserSubprofile(
          action.payload,
          currentSubprofileId
        );

        // API does not return display name and first letter for the owner's profile
        const ownerSubprofile = subprofiles.find(
          (subprofile) => subprofile.Owner
        );
        if (ownerSubprofile && !ownerSubprofile?.DisplayName) {
          ownerSubprofile.DisplayName = currentSubProfile?.DisplayName;
          ownerSubprofile.FirstLetter = currentSubProfile?.FirstLetter;
        }

        return createUserSubprofilesSuccess(subprofiles);
      } catch (error) {
        return createUserSubprofilesFailure(error as IErrorModel);
      }
    })
  );

const updateUserSubprofileEpic = (
  action$: Observable<IEditUserSubprofileAction>,
  state$: StateObservable<IAppState>
) =>
  action$.pipe(
    ofType(Consts.EDIT_USER_SUBPROFILES),
    switchMap(async (action: IEditUserSubprofileAction) => {
      try {
        const prevSubprofile =
          state$.value.user.subprofiles.Data?.subprofiles?.find(
            (subprofile) => subprofile.UserId === action.id
          );

        const user = await DataProvider.updateUserSubprofile(
          { ...prevSubprofile, ...action.payload },
          action.id
        );

        return updateUserSubprofileSuccess(user);
      } catch (error) {
        return updateUserSubprofileFailure(error as IErrorModel);
      }
    })
  );

const deleteUserSubprofileEpic = (
  action$: Observable<IDeleteUserSubprofilesAction>
) =>
  action$.pipe(
    ofType(Consts.DELETE_USER_SUBPROFILES),
    switchMap(async (action: IDeleteUserSubprofilesAction) => {
      try {
        await DataProvider.deleteUserSubprofile(action.payload);

        return deleteUserSubprofileSuccess(action.payload);
      } catch (error) {
        return deleteUserSubprofileFailure(error as IErrorModel);
      }
    })
  );

const selectUserSubprofileEpic = (
  action$: Observable<ISelectUserSubprofileAction>
) =>
  action$.pipe(
    ofType(Consts.SELECT_USER_SUBPROFILE),
    switchMap(async (action: ISelectUserSubprofileAction) => {
      try {
        const newTokens = await DataProvider.selectUserSubprofile(
          action.payload.profile.UserId ?? "",
          action.payload.remember
        );

        return selectUserSubprofileSuccess(action.payload.profile, newTokens);
      } catch (error) {
        return selectUserSubprofileFailure(error as IErrorModel);
      }
    })
  );

const selectUserSubprofileSuccessEpic = (
  action$: Observable<ISelectUserSubprofileSuccessAction>,
  state$: StateObservable<IAppState>
) =>
  action$.pipe(
    ofType(Consts.SELECT_USER_SUBPROFILE_SUCCESS),
    mergeMap((action) => {
      const isKid = action.payload.profile.Child;
      const newTokens = action.payload.tokens;
      const result: AnyAction[] = [
        updateTokens(newTokens),
        deleteAllMediaAction(),
        updateApplicationMenu(isKid),
      ];

      if (!state$.value.auth.session?.IsOverTheDeviceLimit) {
        result.push(getUserFavorites());
      }

      return result;
    })
  );

const getUserParentalRatingsEpic = (
  action$: Observable<IGetUserParentalRatingsAction>
) =>
  action$.pipe(
    ofType(Consts.GET_USER_PARENTAL_RATINGS),
    switchMap(async (action) => {
      try {
        const countryCode = action.payload;
        const result = await DataProvider.getUserParentalRatings(
          countryCode.toUpperCase()
        );
        return getUserParentalRatingsSuccess(result);
      } catch (error) {
        return getUserParentalRatingsFailure(error as IErrorModel);
      }
    })
  );

const setUserSubprofilePinCodeEpic = (
  action$: Observable<ISetUserSubprofilePinCodeAction>,
  state$: StateObservable<IAppState>
) =>
  action$.pipe(
    ofType(Consts.SET_USER_SUBPROFILE_PIN_CODE),
    switchMap(async (action: ISetUserSubprofilePinCodeAction) => {
      try {
        const pinCodeId = state$.value.user.subprofiles.Data?.auth?.PinCodeId;

        const updatedPinCodeId = await DataProvider.setUserSubprofilePinCode(
          action.payload,
          pinCodeId
        );

        return setUserSubprofilePinCodeSuccess(updatedPinCodeId);
      } catch (error) {
        return setUserSubprofilePinCodeFailure(error as IErrorModel);
      }
    })
  );

const deleteUserSubprofilePinCodeEpic = (
  action$: Observable<IDeleteUserSubprofilePinCodeAction>,
  state$: StateObservable<IAppState>
) =>
  action$.pipe(
    ofType(Consts.DELETE_USER_SUBPROFILE_PIN_CODE),
    switchMap(async () => {
      try {
        const pinCodeId = state$.value.user.subprofiles.Data?.auth?.PinCodeId;

        if (!pinCodeId) throw new Error("Pin code does not exist!");

        await DataProvider.deleteUserSubprofilePinCode(pinCodeId);

        return deleteUserSubprofilePinCodeSuccess();
      } catch (error) {
        return deleteUserSubprofilePinCodeFailure(error as IErrorModel);
      }
    })
  );

const checkOrderAndFetchSubscriptionsEpic = (
  action$: Observable<ICheckOrderAndFetchSubscriptionsAction>
) =>
  action$.pipe(
    ofType(Consts.CHECK_ORDER_AND_FETCH_SUBSCRIPTIONS),
    switchMap((action) =>
      defer(() => DataProvider.getOrderStatus(action.orderId)).pipe(
        repeat({ delay: 2000, count: 15 }),
        takeWhile(
          (response) =>
            response.Order.Status === OrderStatus.DRAFT ||
            response.Order.Status === OrderStatus.PENDING,
          true
        ),
        last(),
        mergeMap((response) => {
          if (
            response.Order.Status === OrderStatus.DRAFT ||
            response.Order.Status === OrderStatus.PENDING
          ) {
            return of(
              checkOrderAndFetchSubscriptionsFailure({
                Message: "Failed to get order status",
              })
            );
          }
          return of(
            checkOrderAndFetchSubscriptionsSuccess(),
            getUserSubscriptions()
          );
        }),
        catchError(() =>
          of(
            checkOrderAndFetchSubscriptionsFailure({
              Message: "Failed to get order status",
            })
          )
        )
      )
    )
  );

const getUserCountryEpic = (action$: Observable<IGetUserCountryAction>) =>
  action$.pipe(
    ofType(Consts.GET_USER_COUNTRY),
    switchMap(async () => {
      try {
        const country = await DataProvider.getUserCountry();

        return getUserCountrySuccess(country);
      } catch (error) {
        return getUserCountryFailure(error as IErrorModel);
      }
    })
  );

export const userEpics = [
  getUserFavoritesEpic,
  getProfileEpic,
  updateProfileEpic,
  updateProfileSuccessEpic,
  getUserDevicesEpic,
  deleteUserDeviceEpic,
  deleteUserDeviceSuccessEpic,
  getUserConsentsEpic,
  updateUserConsentsEpic,
  getWatchHistoryEpic,
  deleteWatchHistoryEpic,
  getUserSubscriptionsEpic,
  getUserAvailabilityKeysEpic,
  getUserSubprofilesEpic,
  createUserSubprofileEpic,
  updateUserSubprofileEpic,
  deleteUserSubprofileEpic,
  selectUserSubprofileEpic,
  selectUserSubprofileSuccessEpic,
  getUserParentalRatingsEpic,
  setUserSubprofilePinCodeEpic,
  deleteUserSubprofilePinCodeEpic,
  getProductOfferingIdsEpic,
  checkOrderAndFetchSubscriptionsEpic,
  getUserCountryEpic,
];
